import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  Trophy,
  Crown,
  Medal,
  Award,
  Zap,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Star,
  Upload,
  Sparkles,
  ChevronUp,
  Timer,
  Shield
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line,
  Bar
} from 'recharts';
import { toast } from 'sonner';

// Badge configurations with professional designs
const BADGES = {
  champion: { icon: Crown, label: 'Top Performer', color: 'from-amber-500 to-amber-600', glow: 'shadow-amber-500/30' },
  elite: { icon: Medal, label: 'Elite', color: 'from-slate-500 to-slate-600', glow: 'shadow-slate-500/30' },
  warrior: { icon: Award, label: 'Bronze', color: 'from-orange-500 to-orange-600', glow: 'shadow-orange-500/30' },
  rising: { icon: TrendingUp, label: 'Rising', color: 'from-blue-500 to-blue-600', glow: 'shadow-blue-500/30' },
  consistent: { icon: Target, label: 'Consistent', color: 'from-emerald-500 to-emerald-600', glow: 'shadow-emerald-500/30' },
  highPerformer: { icon: Zap, label: 'High Performer', color: 'from-indigo-500 to-indigo-600', glow: 'shadow-indigo-500/30' }
};

// Memoized Top 3 Podium Component - Prevents re-render if topThree hasn't changed
const PodiumSection = memo(({ topThree }) => {
  if (!topThree || topThree.length < 3) return null;
  
  const podiumOrder = [topThree[1], topThree[0], topThree[2]]; // 2nd, 1st, 3rd
  const heights = ['h-28', 'h-36', 'h-24'];
  const positions = ['#2', '#1', '#3'];
  const gradients = [
    'from-slate-300 to-slate-400',
    'from-yellow-300 via-yellow-400 to-amber-500',
    'from-amber-600 to-amber-700'
  ];
  const glows = ['', 'shadow-2xl shadow-yellow-500/30', ''];
  
  return (
    <div className="flex items-end justify-center gap-4 mb-8 pt-8">
      {podiumOrder.map((user, index) => (
        <div key={user?.user_id || index} className="flex flex-col items-center">
          {/* Avatar & Name */}
          <div className={`relative mb-2 ${index === 1 ? 'transform scale-110' : ''}`}>
            {index === 1 && (
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 animate-bounce">
                <Crown className="w-8 h-8 text-yellow-500 drop-shadow-lg" />
              </div>
            )}
            <Avatar className={`w-16 h-16 border-4 ${index === 1 ? 'border-yellow-400 ring-4 ring-yellow-200' : index === 0 ? 'border-slate-300' : 'border-amber-600'}`}>
              <AvatarFallback className={`bg-gradient-to-br ${gradients[index]} text-white text-lg font-bold`}>
                {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            {index === 0 && <Medal className="absolute -bottom-1 -right-1 w-6 h-6 text-slate-400" />}
            {index === 2 && <Award className="absolute -bottom-1 -right-1 w-6 h-6 text-amber-700" />}
          </div>
          <p className={`font-semibold text-sm mb-1 ${index === 1 ? 'text-lg' : ''}`}>{user?.name?.split(' ')[0] || 'Unknown'}</p>
          <p className={`text-2xl font-bold ${index === 1 ? 'text-yellow-600 text-3xl' : 'text-muted-foreground'}`}>{user?.sales_count || 0}</p>
          
          {/* Podium */}
          <div className={`${heights[index]} w-24 mt-2 rounded-t-xl bg-gradient-to-b ${gradients[index]} ${glows[index]} flex items-end justify-center pb-2`}>
            <span className={`text-2xl font-black ${index === 1 ? 'text-yellow-900' : 'text-white/80'}`}>{positions[index]}</span>
          </div>
        </div>
      ))}
    </div>
  );
});
PodiumSection.displayName = 'PodiumSection';

// Memoized Leaderboard Row Component - Only re-renders when user data changes
const LeaderboardRow = memo(({ user, rank, isCurrentUser }) => {
  const getBadge = useCallback(() => {
    if (rank === 1) return BADGES.champion;
    if (rank === 2) return BADGES.elite;
    if (rank === 3) return BADGES.warrior;
    if (user.avg_per_working_day >= 5) return BADGES.highPerformer;
    if (user.avg_per_working_day >= 3) return BADGES.consistent;
    if (rank <= 5) return BADGES.rising;
    return null;
  }, [rank, user.avg_per_working_day]);
  
  const badge = getBadge();
  const BadgeIcon = badge?.icon;
  
  const rankStyle = useMemo(() => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg shadow-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-slate-300 to-slate-400 text-white';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
    return 'bg-muted text-muted-foreground';
  }, [rank]);
  
  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
      isCurrentUser ? 'bg-primary/10 border-2 border-primary' : 'bg-muted/30 hover:bg-muted/50'
    } ${rank <= 3 ? 'shadow-lg' : ''}`}>
      {/* Rank */}
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${rankStyle}`}>
        {rank <= 3 ? (
          rank === 1 ? <Crown className="w-6 h-6" /> : rank === 2 ? <Medal className="w-6 h-6" /> : <Award className="w-6 h-6" />
        ) : rank}
      </div>
      
      {/* Avatar */}
      <Avatar className="w-12 h-12 border-2 border-background">
        <AvatarFallback className={`bg-gradient-to-br ${
          rank === 1 ? 'from-yellow-400 to-amber-500' : 
          rank === 2 ? 'from-slate-300 to-slate-500' :
          rank === 3 ? 'from-amber-500 to-amber-700' :
          'from-indigo-400 to-purple-500'
        } text-white font-bold`}>
          {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>
      
      {/* Name & Badge */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold truncate">{user.name}</p>
          {badge && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${badge.color} ${badge.glow} shadow-lg`}>
              <BadgeIcon className="w-3 h-3" />
              {badge.label}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{user.role}</p>
      </div>
      
      {/* Stats */}
      <div className="text-right">
        <p className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          {user.sales_count}
        </p>
        <div className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
          <Zap className="w-3 h-3 text-amber-500" />
          {user.avg_per_working_day}/day
        </div>
      </div>
      
      {/* Trend Indicator */}
      <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${
        user.avg_per_working_day >= 3 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 
        'bg-muted text-muted-foreground'
      }`}>
        {user.avg_per_working_day >= 3 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        <span className="text-sm font-medium">
          {user.avg_per_working_day >= 3 ? 'Strong' : 'Steady'}
        </span>
      </div>
    </div>
  );
});
LeaderboardRow.displayName = 'LeaderboardRow';

const TeamPage = () => {
  const { user, api, isAdmin, isManager } = useAuth();
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [dateWiseSales, setDateWiseSales] = useState([]);
  const [offerStats, setOfferStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [teamStats, setTeamStats] = useState(null);

  const COLORS = useMemo(() => ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#10b981', '#3b82f6'], []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const days = selectedPeriod === '7d' ? 7 : selectedPeriod === '30d' ? 30 : 90;
      
      const [teamRes, salesRes, offerRes, statsRes] = await Promise.all([
        api.get('/dashboard/team-performance'),
        api.get(`/dashboard/date-wise-sales?days=${days}`),
        api.get('/dashboard/offer-stats'),
        api.get('/dashboard/sales-stats')
      ]);
      
      setTeamPerformance(teamRes.data);
      setDateWiseSales(salesRes.data || []);
      setOfferStats(offerRes.data || []);
      setTeamStats(statsRes.data);
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [api, selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoize expensive calculations
  const totalSales = useMemo(() => teamPerformance.reduce((acc, u) => acc + (u.sales_count || 0), 0), [teamPerformance]);
  const avgPerAgent = useMemo(() => teamPerformance.length > 0 ? (totalSales / teamPerformance.length).toFixed(1) : 0, [totalSales, teamPerformance.length]);
  const totalUploads = useMemo(() => teamPerformance.reduce((acc, u) => acc + (u.uploads_this_month || 0), 0), [teamPerformance]);
  const topThree = useMemo(() => teamPerformance.slice(0, 3), [teamPerformance]);
  const bestAvgPerformer = useMemo(() => {
    return teamPerformance.filter(u => u.avg_per_working_day > 0).sort((a, b) => b.avg_per_working_day - a.avg_per_working_day)[0] || null;
  }, [teamPerformance]);

  return (
    <div className="p-6 space-y-6 min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-slate-950 dark:via-indigo-950/20 dark:to-purple-950/20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-4xl font-black bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Team Analytics
            </h1>
            <span className="px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-xs font-bold shadow-lg shadow-purple-500/30">
              Performance Hub
            </span>
          </div>
          <p className="text-muted-foreground mt-1">Real-time team performance metrics and insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <TabsList className="bg-white/80 dark:bg-slate-800/80 shadow-lg">
              <TabsTrigger value="7d" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">7 Days</TabsTrigger>
              <TabsTrigger value="30d" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">30 Days</TabsTrigger>
              <TabsTrigger value="90d" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white">90 Days</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={fetchData} variant="outline" className="gap-2 shadow-lg">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="border-0 shadow-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg"><Trophy className="w-6 h-6" /></div>
              <div>
                <p className="text-white/80 text-xs">Total Sales</p>
                <p className="text-3xl font-black">{totalSales}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl bg-gradient-to-br from-pink-500 to-rose-600 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg"><Upload className="w-6 h-6" /></div>
              <div>
                <p className="text-white/80 text-xs">Uploads</p>
                <p className="text-3xl font-black">{totalUploads}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg"><Target className="w-6 h-6" /></div>
              <div>
                <p className="text-white/80 text-xs">Avg/Agent</p>
                <p className="text-3xl font-black">{avgPerAgent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg"><Users className="w-6 h-6" /></div>
              <div>
                <p className="text-white/80 text-xs">Team Size</p>
                <p className="text-3xl font-black">{teamPerformance.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg"><TrendingUp className="w-6 h-6" /></div>
              <div>
                <p className="text-white/80 text-xs">Growth</p>
                <p className="text-3xl font-black">{teamStats?.growth_percentage || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Leaderboard Section */}
        <div className="col-span-7">
          <Card className="border-0 shadow-2xl overflow-hidden bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
            <CardHeader className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Trophy className="w-7 h-7" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-black">Sales Leaderboard</CardTitle>
                    <p className="text-white/80 text-sm">Top performers this month</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
                  <Timer className="w-4 h-4" />
                  <span className="text-sm font-medium">{teamStats?.month_period || 'This Month'}</span>
                </div>
              </div>
            </CardHeader>
            
            {/* Podium */}
            {topThree.length >= 3 && <PodiumSection topThree={topThree} />}
            
            <CardContent className="p-6">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                    </div>
                  ) : (
                    teamPerformance.map((member, index) => (
                      <LeaderboardRow 
                        key={member.user_id} 
                        user={member} 
                        rank={index + 1}
                        isCurrentUser={member.user_id === user?.id}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="col-span-5 space-y-6">
          {/* Date-wise Sales Chart */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-violet-500 to-purple-600 text-white">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                <CardTitle className="text-lg">Date-wise Sales Count</CardTitle>
              </div>
              <p className="text-white/70 text-sm">Daily sales performance over selected period</p>
            </CardHeader>
            <CardContent className="p-4 bg-white dark:bg-slate-900">
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={dateWiseSales}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255,255,255,0.95)', 
                      borderRadius: '12px', 
                      border: 'none',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                    }} 
                  />
                  <Bar dataKey="sales" fill="url(#salesGradient)" radius={[6, 6, 0, 0]} name="Sales Count" />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-violet-500" />
                  <span className="text-xs text-muted-foreground">Sales Count</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sales by Offer Chart */}
          <Card className="border-0 shadow-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <CardTitle className="text-lg">Sales by Offer</CardTitle>
              </div>
              <p className="text-white/70 text-sm">Distribution breakdown</p>
            </CardHeader>
            <CardContent className="p-4 bg-white dark:bg-slate-900">
              {offerStats && offerStats.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={offerStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="offer_name"
                      label={({ offer_name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {offerStats.map((entry, index) => (
                        <Cell key={entry.offer_name || index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255,255,255,0.95)', 
                        borderRadius: '12px', 
                        border: 'none',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
                      }}
                      formatter={(value, name) => [value, name]}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={50}
                      formatter={(value) => <span className="text-xs">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                  <p>No offer data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recognition Section */}
      <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 text-white">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/10 rounded-2xl">
                <Star className="w-10 h-10 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-2xl font-black">Monthly Recognition</h3>
                <p className="text-white/70">Celebrating our top performers</p>
              </div>
            </div>
            
            <div className="flex items-center gap-8">
              {/* Top Performer */}
              {topThree[0] && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <Avatar className="w-20 h-20 border-4 border-yellow-400 ring-4 ring-yellow-400/30">
                      <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-amber-600 text-white text-2xl font-bold">
                        {topThree[0].name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 text-yellow-400" />
                  </div>
                  <p className="mt-2 font-bold">{topThree[0].name}</p>
                  <p className="text-yellow-400 font-black text-xl">{topThree[0].sales_count} Sales</p>
                  <span className="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-bold">
                    <Crown className="w-3 h-3" /> Top Performer
                  </span>
                </div>
              )}
              
              {/* Best Average */}
              {bestAvgPerformer && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <Avatar className="w-20 h-20 border-4 border-cyan-400 ring-4 ring-cyan-400/30">
                      <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-blue-600 text-white text-2xl font-bold">
                        {bestAvgPerformer.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Zap className="absolute -top-2 -right-2 w-7 h-7 text-cyan-400" />
                  </div>
                  <p className="mt-2 font-bold">{bestAvgPerformer.name}</p>
                  <p className="text-cyan-400 font-black text-xl">{bestAvgPerformer.avg_per_working_day}/day</p>
                  <span className="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-300 text-xs font-bold">
                    <Zap className="w-3 h-3" /> Best Daily Avg
                  </span>
                </div>
              )}
              
              {/* Most Consistent */}
              {topThree[1] && (
                <div className="text-center">
                  <div className="relative inline-block">
                    <Avatar className="w-20 h-20 border-4 border-emerald-400 ring-4 ring-emerald-400/30">
                      <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-green-600 text-white text-2xl font-bold">
                        {topThree[1].name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <Shield className="absolute -top-2 -right-2 w-7 h-7 text-emerald-400" />
                  </div>
                  <p className="mt-2 font-bold">{topThree[1].name}</p>
                  <p className="text-emerald-400 font-black text-xl">{topThree[1].sales_count} Sales</p>
                  <span className="inline-flex items-center gap-1 mt-1 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold">
                    <Shield className="w-3 h-3" /> Runner Up
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamPage;
